import React, { useMemo, useState, useEffect } from "react";
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Nav, NavItem, NavLink, TabContent, TabPane,
    Table, Form, FormGroup, Label, Input, Row, Col, Card, CardBody,
    Collapse, Spinner, Badge
} from "reactstrap";
import classNames from "classnames";
import CEEligibilityPanel from "./ClientEligibilityPanel";

function cents(amountCents, currency) {
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" })
            .format((amountCents || 0) / 100);
    } catch (e) {
        return `${(amountCents || 0) / 100} ${currency || ""}`.trim();
    }
}

function formatMoney(amount, currency = "USD") {
    try {
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency,
        }).format(amount ?? 0);
    } catch {
        return `${amount ?? 0} ${currency}`;
    }
}

function toDate(d) {
    // Soporta ISO con o sin timezone
    return new Date(d);
}

function StatusPill({ label, colorHex }) {
    const dot = (
        <span
            style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: 999,
                background: colorHex || "#6c757d",
                marginRight: 6,
                verticalAlign: "middle",
            }}
        />
    );
    return (
        <span className="text-muted">
            {dot}
            <span className="text-body">{label || "Unknown"}</span>
        </span>
    );
}

export default function BaxRxPlusManager({
    isOpen,
    onClose,
    user,
    // Data
    status,
    eligibilityNote,
    billingHistory = {
        data: [],
        totalItems: 0,
        pageSize: 10,
        page: 0,
        hasMore: false
    },
    paymentMethod,
    subscriptionCancelAtPeriodEnd,
    subscriptionExpirationDate,
    dexSchedule,
    dexScheduleLoading,

    // Actions
    onUpdateEligibility,
    onUpdatePaymentMethod,
    onCancelSubscription,
    onCancelImmediately,
    onReactivateSubscription,
    onGrantFreePass,
    onRequestBillingPage,
    onRefreshDexSchedule,
    billingLoading,
    billingError
}) {
    const [activeTab, setActiveTab] = useState("overview");
    const [eligibilityOpen, setEligibilityOpen] = useState(false);
    const [eligibilityDraft, setEligibilityDraft] = useState(eligibilityNote || "");
    const [saving, setSaving] = useState(false);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === "dex" && onRefreshDexSchedule) {
            onRefreshDexSchedule();
        }
    };

    const isActive = status === "active";
    const isCancelling = subscriptionCancelAtPeriodEnd === true && isActive;
    const canReactivate = ["canceled", "past_due", "incomplete", "none", "inactive"].includes(status) || isCancelling;

    console.log("BaxRxPlusManager - status:", status);
    console.log("BaxRxPlusManager - isActive:", isActive);
    console.log("BaxRxPlusManager - canReactivate:", canReactivate);

    const currentPage = billingHistory?.page ?? 0;
    const pageSize = billingHistory?.pageSize ?? 10;
    const totalItems = billingHistory?.totalItems ?? billingHistory?.total ?? billingHistory?.data?.length ?? 0;
    const totalPages = billingHistory?.totalPages ?? Math.max(1, Math.ceil(totalItems / pageSize));
    const hasPrev = currentPage > 0;
    const hasNext = billingHistory?.hasMore ?? (currentPage + 1 < totalPages && (billingHistory?.data?.length === pageSize));

    const showingStart = totalItems === 0 ? 0 : currentPage * pageSize + 1;
    const showingEnd = Math.min((currentPage + 1) * pageSize, totalItems);

    async function goPrev() {
        if (!hasPrev || !onRequestBillingPage) return;
        await onRequestBillingPage(currentPage - 1);
    }
    async function goNext() {
        if (!hasNext || !onRequestBillingPage) return;
        await onRequestBillingPage(currentPage + 1);
    }

    const pmLabel = useMemo(() => {
        if (!paymentMethod) return "No payment method on file";
        const brand = (paymentMethod.brand || "").toUpperCase();
        const last4 = paymentMethod.last4 || "••••";
        const expMonth = paymentMethod.expMonth || "--";
        const expYear = paymentMethod.expYear || "----";
        return `${brand} •••• ${last4}  exp ${expMonth}/${expYear}`;
    }, [paymentMethod]);

    async function handleSaveEligibility() {
        if (!onUpdateEligibility) {
            setEligibilityOpen(false);
            return;
        }
        setSaving(true);
        try {
            await onUpdateEligibility(eligibilityDraft.trim());
            setEligibilityOpen(false);
        } finally {
            setSaving(false);
        }
    }

    async function handleCancel() {
        if (!onCancelSubscription) return;
        setSaving(true);
        try {
            await onCancelSubscription();
        } finally {
            setSaving(false);
        }
    }

    async function handleCancelImmediately() {
        if (!onCancelImmediately) return;
        setSaving(true);
        try {
            await onCancelImmediately();
        } finally {
            setSaving(false);
        }
    }

    async function handleReactivate() {
        if (!onReactivateSubscription) return;
        setSaving(true);
        try {
            await onReactivateSubscription();
        } finally {
            setSaving(false);
        }
    }

    async function handleUpdatePayment() {
        if (!onUpdatePaymentMethod) return;
        // Aquí deberías abrir tu flujo del PSP (Stripe, etc.) para obtener un token o pmId
        const dummyToken = "pm_test_token_123";
        setSaving(true);
        try {
            await onUpdatePaymentMethod(dummyToken);
        } finally {
            setSaving(false);
        }
    }

    async function handleGrantFreePass() {
        if (!onGrantFreePass) return;
        setSaving(true);
        try {
            await onGrantFreePass();
        } finally {
            setSaving(false);
        }
    }

    return (
        <>
            <Modal isOpen={isOpen} toggle={onClose} size="lg">
                <ModalHeader toggle={onClose}>
                    BaxRx+ — Manage Subscription
                </ModalHeader>
                <ModalBody>
                    <Nav pills className="mb-3">
                        <NavItem>
                            <NavLink
                                style={{ cursor: "pointer" }}
                                className={classNames({ active: activeTab === "overview" })} onClick={() => handleTabChange("overview")}>
                                Overview
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                style={{ cursor: "pointer" }}
                                className={classNames({ active: activeTab === "billing" })} onClick={() => handleTabChange("billing")}>
                                Billing History
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                style={{ cursor: "pointer" }}
                                className={classNames({ active: activeTab === "payment" })} onClick={() => handleTabChange("payment")}>
                                Payment Method
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                style={{ cursor: "pointer" }}
                                className={classNames({ active: activeTab === "actions" })} onClick={() => handleTabChange("actions")}>
                                Actions
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                style={{ cursor: "pointer" }}
                                className={classNames({ active: activeTab === "dex" })} onClick={() => handleTabChange("dex")}>
                                DEX Info
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <TabContent activeTab={activeTab}>
                        {/* OVERVIEW */}
                        <TabPane tabId="overview">
                            <Row>
                                <Col md={6}>
                                    <Card className="border">
                                        <CardBody>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="text-muted">Status</div>
                                                    <div className="fs-6 fw-semibold text-capitalize">{String(status || "none").replace("_", " ")}</div>
                                                </div>
                                                <i className="ri-shield-star-line fs-3 text-info" />
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                                {/* <Col md={6}>
                                    <Card className="border">
                                        <CardBody>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="text-muted">Eligibility</div>
                                                    <div className="fs-6">{eligibilityNote || "—"}</div>
                                                </div>
                                                <Button size="sm" color="outline-primary" onClick={() => setEligibilityOpen(true)}>
                                                    Edit
                                                </Button>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col> */}
                            </Row>

                            <div className="mt-3">
                                <CEEligibilityPanel
                                    userId={user.id}
                                    onUpdated={(updated) => {
                                        // si querés refrescar algo del manager después de guardar fechas
                                        // ej: toast, refetch, update local, etc.
                                    }}
                                />
                            </div>
                        </TabPane>

                        {/* BILLING */}
                        <TabPane tabId="billing">
                            {billingError ? (
                                <div className="alert alert-danger py-2">{String(billingError)}</div>
                            ) : null}

                            {!billingHistory?.data || billingHistory.data.length === 0 ? (
                                <div className="text-center text-muted py-3">No invoices yet</div>
                            ) : (
                                <div className="mb-2">
                                    {/* Summary + Pagination */}
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="text-muted small">
                                            Invoices: <strong>{totalItems}</strong>
                                            <span className="ms-2">
                                                • Showing <strong>{showingStart}</strong>–<strong>{showingEnd}</strong> of <strong>{totalItems}</strong>
                                            </span>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            <div className="text-muted small me-2">
                                                Last:{" "}
                                                <strong>
                                                    {new Date(
                                                        [...billingHistory.data].sort((a, b) => +toDate(b.date) - +toDate(a.date))[0].date
                                                    ).toLocaleDateString()}
                                                </strong>
                                            </div>

                                            <Button
                                                size="sm"
                                                color="soft-secondary"
                                                className="border"
                                                disabled={!hasPrev || billingLoading}
                                                onClick={goPrev}
                                            >
                                                Prev
                                            </Button>
                                            <Button
                                                size="sm"
                                                color="primary"
                                                disabled={!hasNext || billingLoading}
                                                onClick={goNext}
                                            >
                                                Next
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Compact list */}
                                    <div className="d-flex flex-column gap-1">
                                        {billingHistory.data.map((inv) => (
                                            <InvoiceItem key={inv.id} invoice={inv} />
                                        ))}
                                    </div>

                                    {/* Tiny page indicator */}
                                    <div className="text-muted small mt-2">
                                        Page <strong>{currentPage + 1}</strong> of <strong>{totalPages}</strong>
                                    </div>
                                </div>
                            )}
                        </TabPane>

                        {/* PAYMENT */}
                        <TabPane tabId="payment">
                            <Card className="border">
                                <CardBody>
                                    <div className="mb-3">
                                        <div className="text-muted">Current method</div>
                                        <div className="fs-6">{pmLabel}</div>
                                    </div>
                                    {/* Update payment method button hidden as per requirements */}
                                    {/* <Button color="primary" onClick={handleUpdatePayment} disabled={saving}>
                                        <i className="ri-bank-card-line me-2" />
                                        Update Payment Method
                                    </Button> */}
                                </CardBody>
                            </Card>
                        </TabPane>

                        {/* ACTIONS */}
                        <TabPane tabId="actions">
                            <div className="d-flex flex-column gap-3">
                                {/* Main Actions */}
                                <div>
                                    <h6 className="mb-2">Subscription Management</h6>
                                    <div className="d-flex gap-2 flex-wrap">
                                        <Button color="danger" outline onClick={handleCancel} disabled={!isActive || isCancelling || saving}>
                                            <i className="ri-close-circle-line me-2" />
                                            Cancel at Period End
                                        </Button>
                                        <Button color="danger" onClick={handleCancelImmediately} disabled={!isActive || saving}>
                                            <i className="ri-close-circle-fill me-2" />
                                            Cancel Immediately
                                        </Button>
                                        <Button color="success" outline onClick={handleReactivate} disabled={!canReactivate || saving}>
                                            <i className="ri-refresh-line me-2" />
                                            {isCancelling ? "Resume Subscription" : "Reactivate"}
                                        </Button>
                                    </div>
                                    <div className="text-muted small mt-2">
                                        <div><strong>Cancel at Period End:</strong> Subscription will remain active until the end of the billing period</div>
                                        <div><strong>Cancel Immediately:</strong> Terminates subscription and access right away</div>
                                    </div>
                                </div>

                                {isCancelling && subscriptionExpirationDate && (
                                    <div className="alert alert-warning py-2 mb-0">
                                        <i className="ri-alarm-warning-line me-2"></i>
                                        <strong>Subscription Cancelling</strong>
                                        <div className="small mt-1">
                                            Will expire on: {new Date(subscriptionExpirationDate).toLocaleDateString()}
                                        </div>
                                    </div>
                                )}

                                {!isActive && (
                                    <div className="border-top pt-3">
                                        <div className="mb-2">
                                            <strong>Grant Free Access</strong>
                                            <div className="text-muted small">
                                                Give this user free access to BaxRx+ without requiring an active subscription.
                                            </div>
                                        </div>
                                        <Button
                                            color="primary"
                                            outline
                                            onClick={handleGrantFreePass}
                                            disabled={saving || !onGrantFreePass}
                                        >
                                            <i className="ri-vip-crown-line me-2" />
                                            Grant Free Pass
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </TabPane>

                        {/* DEX INFO */}
                        <TabPane tabId="dex">
                            {dexScheduleLoading ? (
                                <div className="text-center py-5">
                                    <Spinner color="primary" />
                                    <p className="mt-3 text-muted">Loading DEX schedule...</p>
                                </div>
                            ) : !dexSchedule ? (
                                <div className="text-center py-5">
                                    <i className="ri-information-line text-muted mb-3" style={{ fontSize: "3rem" }}></i>
                                    <p className="text-muted">No DEX schedule information available</p>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-3">
                                    {/* DEX Schedule Info */}
                                    <Card className="border">
                                        <CardBody>
                                            <div className="mb-3">
                                                <h6 className="mb-0">DEX Schedule Details</h6>
                                            </div>
                                            <Row>
                                                <Col md={6}>
                                                    <div className="mb-2">
                                                        <small className="text-muted">Schedule ID</small>
                                                        <div className="fw-medium">{dexSchedule.schedule?.schedule_id || 'N/A'}</div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <small className="text-muted">Customer ID</small>
                                                        <div className="fw-medium">{dexSchedule.schedule?.customer_id || 'N/A'}</div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <small className="text-muted">Environment</small>
                                                        <div>
                                                            <Badge color={
                                                                dexSchedule.schedule?.response?.environment === 'production' ? 'success' :
                                                                dexSchedule.schedule?.response?.environment === 'sandbox' ? 'warning' :
                                                                'secondary'
                                                            }>
                                                                {dexSchedule.schedule?.response?.environment || 'N/A'}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <small className="text-muted">Status</small>
                                                        <div>
                                                            <Badge color={
                                                                dexSchedule.schedule?.schedule_status === 'active' ? 'success' :
                                                                dexSchedule.schedule?.schedule_status === 'suspended' ? 'warning' :
                                                                dexSchedule.schedule?.schedule_status === 'deleted' ? 'danger' :
                                                                'secondary'
                                                            }>
                                                                {dexSchedule.schedule?.schedule_status || 'N/A'}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <small className="text-muted">Frequency</small>
                                                        <div className="fw-medium">{dexSchedule.schedule?.schedule_frequency || 'N/A'}</div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <small className="text-muted">Amount</small>
                                                        <div className="fw-medium">${(dexSchedule.schedule?.schedule_amount || 0).toFixed(2)}</div>
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="mb-2">
                                                        <small className="text-muted">Created Date</small>
                                                        <div className="fw-medium">
                                                            {dexSchedule.schedule?.schedule_created_date
                                                                ? new Date(dexSchedule.schedule.schedule_created_date).toLocaleDateString()
                                                                : 'N/A'}
                                                        </div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <small className="text-muted">Start Date</small>
                                                        <div className="fw-medium">
                                                            {dexSchedule.schedule?.schedule_start_date
                                                                ? new Date(dexSchedule.schedule.schedule_start_date).toLocaleDateString()
                                                                : 'N/A'}
                                                        </div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <small className="text-muted">Customer Token</small>
                                                        <div className="fw-medium text-truncate" style={{maxWidth: '200px'}}>
                                                            {dexSchedule.schedule?.customer_token || 'N/A'}
                                                        </div>
                                                    </div>
                                                    <div className="mb-2">
                                                        <small className="text-muted">Payment Method Token</small>
                                                        <div className="fw-medium text-truncate" style={{maxWidth: '200px'}}>
                                                            {dexSchedule.schedule?.paymethod_token || 'N/A'}
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>

                                    {/* Local Subscription Info */}
                                    {dexSchedule.localSubscriptionInfo && (
                                        <Card className="border">
                                            <CardBody>
                                                <h6 className="mb-3">Local Subscription Info</h6>
                                                <Row>
                                                    <Col md={6}>
                                                        <div className="mb-2">
                                                            <small className="text-muted">Is Active</small>
                                                            <div>
                                                                <Badge color={dexSchedule.localSubscriptionInfo.isSubscriptionActive ? 'success' : 'danger'}>
                                                                    {dexSchedule.localSubscriptionInfo.isSubscriptionActive ? 'Active' : 'Inactive'}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                        <div className="mb-2">
                                                            <small className="text-muted">Status</small>
                                                            <div className="fw-medium">{dexSchedule.localSubscriptionInfo.subscriptionStatus || 'N/A'}</div>
                                                        </div>
                                                        <div className="mb-2">
                                                            <small className="text-muted">Free Pass</small>
                                                            <div>
                                                                <Badge color={dexSchedule.localSubscriptionInfo.subscriptionFreePass ? 'info' : 'secondary'}>
                                                                    {dexSchedule.localSubscriptionInfo.subscriptionFreePass ? 'Yes' : 'No'}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="mb-2">
                                                            <small className="text-muted">Cancel at Period End</small>
                                                            <div>
                                                                <Badge color={dexSchedule.localSubscriptionInfo.subscriptionCancelAtPeriodEnd ? 'warning' : 'secondary'}>
                                                                    {dexSchedule.localSubscriptionInfo.subscriptionCancelAtPeriodEnd ? 'Yes' : 'No'}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                        <div className="mb-2">
                                                            <small className="text-muted">Expiration Date</small>
                                                            <div className="fw-medium">
                                                                {dexSchedule.localSubscriptionInfo.subscriptionExpirationDate
                                                                    ? new Date(dexSchedule.localSubscriptionInfo.subscriptionExpirationDate).toLocaleDateString()
                                                                    : 'N/A'}
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                        </Card>
                                    )}
                                </div>
                            )}
                        </TabPane>
                    </TabContent>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={onClose}>Close</Button>
                </ModalFooter>
            </Modal>

            {/* Eligibility editor */}
            <Modal isOpen={eligibilityOpen} toggle={() => setEligibilityOpen(false)}>
                <ModalHeader toggle={() => setEligibilityOpen(false)}>Update Eligibility</ModalHeader>
                <ModalBody>
                    <Form onSubmit={(e) => { e.preventDefault(); handleSaveEligibility(); }}>
                        <FormGroup>
                            <Label for="elig">Client eligibility notes</Label>
                            <Input
                                id="elig"
                                type="textarea"
                                rows={4}
                                value={eligibilityDraft}
                                onChange={(e) => setEligibilityDraft(e.target.value)}
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setEligibilityOpen(false)}>Cancel</Button>
                    <Button color="primary" onClick={handleSaveEligibility} disabled={saving}>Save</Button>
                </ModalFooter>
            </Modal>
        </>
    );
}

function InvoiceItem({ invoice }) {
    const [open, setOpen] = React.useState(false);

    const {
        id,
        date,
        formattedDate,
        amount,
        statusDisplay,
        statusColor,
        description,
        paymentMethod,
        paymentMethodString,
    } = invoice || {};

    const friendlyDate = formattedDate || (date ? new Date(date).toLocaleDateString() : "—");

    const cardBrand =
        (paymentMethod && paymentMethod.cardType ? paymentMethod.cardType.toUpperCase() : "") ||
        ((paymentMethodString || "").split(" ")[0] || "");
    const last4 =
        (paymentMethod && paymentMethod.cardLastFour
            ? paymentMethod.cardLastFour.replace(/\*/g, "•")
            : null) ||
        (((paymentMethodString || "").match(/(\*{4,}|\d{4})$/) || [])[0] || "••••").replace(/\*/g, "•");

    return (
        <Card className="border rounded-3" style={{ boxShadow: "none" }}>
            <CardBody className="py-1 px-2">
                {/* Header (compact) */}
                <div
                    role="button"
                    onClick={() => setOpen((v) => !v)}
                    className="d-flex align-items-center justify-content-between"
                    style={{ minHeight: 44 }}
                >
                    <div className="d-flex align-items-center gap-2">
                        <div className="text-muted small" style={{ minWidth: 90 }}>
                            {friendlyDate}
                        </div>
                        <div className="fw-semibold" style={{ fontSize: 14 }}>
                            {description || "Subscription Payment"}
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <div className="text-end fw-semibold" style={{ minWidth: 80, fontSize: 14 }}>
                            {formatMoney(amount, "USD")}
                        </div>
                        <div style={{ minWidth: 80 }}>
                            <StatusPill label={statusDisplay || "Paid"} colorHex={statusColor} />
                        </div>
                        <Button
                            size="sm"
                            color="soft-secondary"
                            className="border py-0"
                            style={{ height: 28, lineHeight: "26px" }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setOpen((v) => !v);
                            }}
                        >
                            {open ? "Hide" : "Details"}
                        </Button>
                    </div>
                </div>

                {/* Collapse (compact) */}
                <Collapse isOpen={open}>
                    <hr className="my-2" />
                    <Row className="g-2">
                        <Col md={6}>
                            <div className="text-muted small">Transaction ID</div>
                            <div className="fw-medium" style={{ fontSize: 13 }}>{id}</div>
                        </Col>
                        <Col md={6}>
                            <div className="text-muted small">Amount</div>
                            <div className="fw-medium" style={{ fontSize: 13 }}>{formatMoney(amount, "USD")}</div>
                        </Col>

                        <Col md={6}>
                            <div className="text-muted small">Status</div>
                            <div className="fw-medium" style={{ fontSize: 13 }}>
                                <StatusPill label={statusDisplay || "Paid"} colorHex={statusColor} />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="text-muted small">Date & Time</div>
                            <div className="fw-medium" style={{ fontSize: 13 }}>
                                {date ? new Date(date).toLocaleString() : "—"}
                            </div>
                        </Col>

                        <Col md={6}>
                            <div className="text-muted small">Payment Method</div>
                            <div className="fw-medium" style={{ fontSize: 13 }}>
                                {paymentMethodString || `${cardBrand || "Card"} ending in ${last4}`}
                            </div>
                            {paymentMethod && paymentMethod.type ? (
                                <small className="text-muted d-block">Type: {paymentMethod.type}</small>
                            ) : null}
                        </Col>
                    </Row>
                </Collapse>
            </CardBody>
        </Card>
    );
}